/**
 * Controller state-machine plumbing shared by the movement / physics / ball / contact modules (owner: movement
 * team). Pure helpers, allocation-free.
 *
 * ── Frame counting (state.ts invariant 6) ──────────────────────────────────────────────────────────────────
 * `updateControllers` increments `stateFrame` at the start of every unfrozen tick, then runs the handler of the
 * current state. Two ways to change state:
 *  - `enterNow(p, st)` — from INSIDE the controller: this tick is frame 1 of `st` and the dispatcher immediately
 *    runs `st`'s handler for frame 1 (so a jump/dash/lunge starts on the very tick its button fires).
 *  - `setState(p, st)` — from any module that runs AFTER the controller this tick (integrate, ball, contact, rules,
 *    specials) or before it (rules/specials): `stateFrame = 0`, so the NEXT controller pass is frame 1. Rules and
 *    specials use the same convention (`p.state = X; p.stateFrame = 0`).
 *
 * ── PlayerSim.flags bits owned by the movement/contact modules (bits 0–15; other teams use bits ≥ 16) ──────
 */
import { PlayerState } from '../enums';
import type { PlayerSim } from '../state';

export const PF = {
  /** Fast-falling: integrate lets vy go down to -PHYSICS.fastFallSpeed. Cleared on landing. */
  FAST_FALL: 1 << 0,
  /** The current lunge (DIVE / LEAP_GRAB) was resolved by contact (tackle, break, whiff, decoy pop). */
  LUNGE_DONE: 1 << 1,
  /** The current leap whiffed: its landing uses FRAMES.leapGrab.recovery. */
  LUNGE_WHIFF: 1 << 2,
  /** The current stiff-arm connected (no STIFF_ARM{hit:false} at the end of its active frames). */
  STIFF_HIT: 1 << 3,
  /** THROW_RELEASE already launched the ball. */
  THROWN: 1 << 4,
  /** The current lunge started against a ball carrier (a miss is a WHIFF); otherwise it is a catch lunge. */
  VS_CARRIER: 1 << 5,
  /** This player deflected the current flight: his glove stops tracking it (no re-swat spam). */
  SWATTED: 1 << 6,
  /** The fast-fall of this jump was already used. */
  FAST_FALL_USED: 1 << 7,
} as const;
/** Every flag bit owned by this team (cleared per player at the snap). */
export const PF_MASK = 0xffff;

let transitioned = false;

/** Change state from inside the controller: this tick is frame 1 of `st` (its handler runs this tick). */
export const enterNow = (p: PlayerSim, st: PlayerState): void => {
  p.prevState = p.state;
  p.state = st;
  p.stateFrame = 1;
  if (st !== PlayerState.IDLE && st !== PlayerState.RUN) p.stillFrames = 0;
  transitioned = true;
};

/** Change state from outside the controller: the next controller pass is frame 1 of `st`. */
export const setState = (p: PlayerSim, st: PlayerState): void => {
  p.prevState = p.state;
  p.state = st;
  p.stateFrame = 0;
  p.stillFrames = 0;
};

/** Dispatcher hook: did a handler call `enterNow` since the last call? (resets the flag) */
export const takeTransition = (): boolean => {
  const t = transitioned;
  transitioned = false;
  return t;
};

/** Leave an action/lock state for the neutral state that matches the body (IDLE on the ground, else AIRBORNE). */
export const toNeutral = (p: PlayerSim): void => {
  enterNow(p, p.grounded ? PlayerState.IDLE : PlayerState.AIRBORNE);
};

/** Enter a timed lock (LANDING / STUMBLE / STUNNED) from outside the controller. */
export const setLock = (p: PlayerSim, st: PlayerState, frames: number): void => {
  setState(p, st);
  p.stunFrames = frames;
};

/** States that keep running when the player lands (they are not "airborne" states). */
export const keepsStateOnLanding = (st: PlayerState): boolean =>
  st === PlayerState.CHARGING_THROW ||
  st === PlayerState.THROW_RELEASE ||
  st === PlayerState.PUMP_FAKE ||
  st === PlayerState.CATCH ||
  st === PlayerState.STUNNED ||
  st === PlayerState.TACKLED ||
  st === PlayerState.CELEBRATE ||
  st === PlayerState.HARD_COUNT ||
  st === PlayerState.SPECIAL_POSE ||
  st === PlayerState.STUMBLE ||
  st === PlayerState.LANDING;

/** The player is in a lunge whose hitbox can still connect. */
export const lungeOpen = (p: PlayerSim): boolean => (p.flags & PF.LUNGE_DONE) === 0;
